












































































































































































































































import {
  computed,
  defineComponent,
  PropType,
  ref,
  watch
} from '@vue/composition-api';
import { AxiosError } from 'axios';

import { cloneDeep } from 'lodash';
import { diffDeep, setErrorFromAxiosError } from '@/utils/helpers';
import {
  IWorkflowFormTemplateRevisionAccessCondition,
  IWorkflowFormTemplateRevisionAccessConditionData,
  WorkflowFormTemplateRevisionAccessCondition,
  IAccessConditionTargetData,
  AccessTargetType
} from '@/models';
import { IFieldErrors, ISystemError } from '@/models/error/error.types';
import { ALForm } from '@/common/component.types';
import { requiredValidator } from '@/utils/validators';

import ErrorAlert from '@/components/common/ErrorAlert.vue';
import ALDialog from '@/components/core/Dialog/ALDialog.vue';

import GroupAutoCompletePicker from '@/components/account/GroupAutoCompletePicker.vue';
import WorkflowFormTemplateStakeholderUsersAutoCompletePicker from '@/components/workflow-form-builder/WorkflowFormTemplateStakeholderUsersAutoCompletePicker.vue';
import WorkflowFormBuilderAccessPredicateQueryTreeBuilder from './predicates/WorkflowFormBuilderAccessPredicateQueryTreeBuilder.vue';
import WorkflowFormBuilderRevisionGrantAccessTargetFormDialog from './WorkflowFormBuilderRevisionGrantAccessTargetFormDialog.vue';

import { createNamespacedHelpers, useStore } from 'vuex-composition-helpers';

import { IRootState } from '@/store';
import {
  IWorkflowFormTemplateRevisionActions,
  IWorkflowFormTemplateRevisionGetters,
  IWorkflowFormTemplateRevisionMutations,
  IWorkflowFormTemplateState
} from '@/store/workflow-form-template';

export default defineComponent({
  name: 'WorkflowFormBuilderRevisionGrantAccessFormDialog',
  components: {
    ALDialog,
    ErrorAlert,
    GroupAutoCompletePicker,
    WorkflowFormTemplateStakeholderUsersAutoCompletePicker,
    WorkflowFormBuilderAccessPredicateQueryTreeBuilder,
    WorkflowFormBuilderRevisionGrantAccessTargetFormDialog
  },

  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    isEdit: {
      type: Boolean,
      default: false
    },

    templateId: {
      type: Number,
      required: true
    },

    revisionId: {
      type: Number,
      required: true
    },

    computationList: {
      type: Array as PropType<IWorkflowFormTemplateRevisionAccessCondition[]>,
      default: () => []
    },

    accessComputationRule: {
      type: Object as PropType<IWorkflowFormTemplateRevisionAccessCondition | null>,
      default: null
    }
  },

  emits: ['update:dialog'],

  setup(props, { emit }) {
    const localDialog = computed({
      get: () => props.dialog,
      set: value => {
        emit('update:dialog', value);
      }
    });

    const accessRuleData = ref<
      IWorkflowFormTemplateRevisionAccessConditionData
    >({ nodes: [], targets: [] });

    watch(localDialog, value => {
      if (value) {
        resetFormValidation();
        clearErrors();
        if (!props.isEdit) {
          accessRuleData.value = {
            nodes: [],
            targets: []
          } as IWorkflowFormTemplateRevisionAccessConditionData;
        } else {
          accessRuleData.value = cloneDeep(props.accessComputationRule!);
        }
      }
    });

    // Targets management
    const targetsList = computed(() => {
      return accessRuleData.value.targets || [];
    });

    const targetDialog = ref(false);
    const isEditingTarget = ref(false);
    const editingTargetIndex = ref<number | null>(null);
    const editingTarget = computed<IAccessConditionTargetData | null>(() => {
      if (isEditingTarget.value && editingTargetIndex.value !== null) {
        return targetsList.value[editingTargetIndex.value] || null;
      }
      return null;
    });

    function addTarget() {
      isEditingTarget.value = false;
      editingTargetIndex.value = null;
      targetDialog.value = true;
    }

    function editTarget(index: number) {
      isEditingTarget.value = true;
      editingTargetIndex.value = index;
      targetDialog.value = true;
    }

    function deleteTarget(index: number) {
      if (!accessRuleData.value.targets) {
        accessRuleData.value.targets = [];
      }
      accessRuleData.value.targets.splice(index, 1);
    }

    function handleTargetSave(targetData: IAccessConditionTargetData) {
      if (!accessRuleData.value.targets) {
        accessRuleData.value.targets = [];
      }

      if (isEditingTarget.value && editingTargetIndex.value !== null) {
        // Update existing target
        accessRuleData.value.targets[editingTargetIndex.value] = targetData;
      } else {
        // Add new target
        accessRuleData.value.targets.push(targetData);
      }

      // Reset editing state
      isEditingTarget.value = false;
      editingTargetIndex.value = null;
    }

    const accessRuleRef = ref<ALForm | null>(null);
    const valid = ref(false);

    const subtitle = computed(
      () =>
        `${
          props.isEdit ? 'Update' : 'Create'
        } conditions that determine who can view the form`
    );

    function closeDialog() {
      resetFormValidation();
      clearErrors();
      localDialog.value = false;
    }

    const store = useStore<IRootState>();

    const { useActions } = createNamespacedHelpers<
      IWorkflowFormTemplateState,
      IWorkflowFormTemplateRevisionGetters,
      IWorkflowFormTemplateRevisionActions,
      IWorkflowFormTemplateRevisionMutations
    >(store, 'workflowFormTemplate');

    const { updateWorkflowFormTemplateRevision } = useActions([
      'updateWorkflowFormTemplateRevision'
    ]);

    const loading = ref(false);

    const error = ref<ISystemError | null>(null);

    function clearErrors() {
      error.value = null;
    }

    function resetFormValidation(): void {
      if (accessRuleRef.value) {
        return accessRuleRef.value.resetValidation();
      }
    }

    function validateForm(): boolean {
      let valid = false;
      if (accessRuleRef.value) {
        valid = accessRuleRef.value.validate();
      }
      return valid;
    }

    async function save() {
      clearErrors();
      const valid = await validateForm();

      if (valid) {
        let accessComputationRuleInstance: IWorkflowFormTemplateRevisionAccessCondition | null = null;

        try {
          accessComputationRuleInstance = new WorkflowFormTemplateRevisionAccessCondition(
            accessRuleData.value
          );
        } catch (e) {
          // When constructing the instance before saving the possibility of an error can occur the local error is to store that error instance and display it
          error.value = e as ISystemError;
        }

        if (accessComputationRuleInstance != null) {
          loading.value = true;

          let originalList = cloneDeep(props.computationList);

          // Create a new array from the current list, add the new instance, and update the parent's access computation list
          const updatedList = props.isEdit
            ? originalList.map(computation =>
                computation.id === accessComputationRuleInstance!.id
                  ? accessComputationRuleInstance!
                  : computation
              )
            : [...originalList, accessComputationRuleInstance!];

          // Emit event or set parent's property if possible, here assumes two-way binding prop

          const newList = updatedList; //diffDeep(updatedList, originalList, true);

          return updateWorkflowFormTemplateRevision({
            templateId: props.templateId,
            revision: {
              id: props.revisionId,
              access_conditions: newList
            }
          })
            .then(() => {
              emit('refreshRevision');
              closeDialog();
            })
            .catch((err: AxiosError) => {
              const axiosError = setErrorFromAxiosError(err);

              error.value = axiosError;

              if (axiosError.fieldErrors['access_conditions']) {
                const index = newList.findIndex(
                  computation =>
                    computation.id === accessComputationRuleInstance!.id
                );
                if (index !== -1) {
                  error.value.fieldErrors = ((axiosError.fieldErrors[
                    'access_conditions'
                  ] as unknown) as IFieldErrors[])[index]!;
                } else {
                  error.value.fieldErrors = ((axiosError.fieldErrors[
                    'access_conditions'
                  ] as unknown) as IFieldErrors[])[newList.length - 1]!;
                }
              }
            })
            .finally(() => {
              loading.value = false;
            });
        }
      }
    }

    return {
      localDialog,
      accessRuleData,
      valid,
      accessRuleRef,
      subtitle,
      requiredValidator,
      closeDialog,
      save,
      clearErrors,
      loading,
      error,
      // Targets
      targetsList,
      targetDialog,
      isEditingTarget,
      editingTarget,
      AccessTargetType,
      addTarget,
      editTarget,
      deleteTarget,
      handleTargetSave
    };
  }
});
